import { formatterInputNumber } from "@/utils"
import { Button, DatePicker, Form, InputNumber, Modal, Space } from "antd"
import TextArea from "antd/es/input/TextArea"
import dayjs from "dayjs"
import { useEffect } from "react"

interface UpsertFormProps {
  visible: boolean
  mode: 'create' | 'update'
  initialData?: any
  onCancel: () => void
  onSubmit: (data: any) => Promise<void>
  loading?: boolean
}

const UpsertForm: React.FC<UpsertFormProps> = ({
  visible,
  mode,
  initialData,
  onCancel,
  onSubmit,
  loading = false
}) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (visible && mode === 'update' && initialData) {
      form.setFieldsValue({
        date: dayjs(initialData.date),
        book_revenue: initialData.book_revenue,
        cash_amount: initialData.cash_amount,
        bank_transfer_amount: initialData.bank_transfer_amount,
        notes: initialData.notes,
      })
    }
  }, [visible, mode, initialData, form])

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        date: values.date.format("YYYY-MM-DD"),
      }
      await onSubmit(data)
      form.resetFields()
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  const renderFooter = () => {
    return (
      <Space>
      <Button
        onClick={() => {
          form.resetFields()
        }}
      >
        Làm mới
      </Button>
      <Button
        key="cancel"
        onClick={handleCancel}
      >
        Hủy
      </Button>
      <Button type="primary" onClick={() => form.submit()} loading={loading}>
        {mode === "create" ? "Thêm mới" : "Lưu"}
      </Button>
    </Space>
    )
  }

  return (
    <Modal
      title={mode === "create" ? "Thêm báo cáo dòng tiền" : "Chỉnh sửa báo cáo dòng tiền"}
      open={visible}
      width={600}
      onCancel={handleCancel}
      footer={renderFooter()}
    >
      <Form
        name="wrap"
        labelCol={{ flex: "120px" }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ flex: 1 }}
        colon={false}
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          date: dayjs(new Date()),
        }}
      >
        <Form.Item
          name="date"
          label="Ngày báo cáo"
          rules={[{ required: true, message: "Vui lòng chọn ngày báo cáo!" }]}
        >
          <DatePicker 
            placeholder="Chọn ngày báo cáo" 
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          label="Doanh thu sổ sách:"
          name="book_revenue"
          rules={[{ required: true, pattern: new RegExp(/^[0-9]+$/), message: "Vui lòng nhập dữ liệu số!" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Nhập doanh thu sổ sách"
            formatter={value => formatterInputNumber(value)}
            addonAfter="VNĐ"
            step={10000}
            min={0}
          />
        </Form.Item>

        <Form.Item
          label="Tiền mặt thực tế:"
          name="cash_amount"
          rules={[{ required: true, pattern: new RegExp(/^[0-9]+$/), message: "Vui lòng nhập dữ liệu số!" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Nhập tiền mặt thực tế"
            formatter={value => formatterInputNumber(value)}
            addonAfter="VNĐ"
            step={10000}
            min={0}
          />
        </Form.Item>

        <Form.Item
          label="Chuyển khoản thực tế:"
          name="bank_transfer_amount"
          rules={[{ required: true, pattern: new RegExp(/^[0-9]+$/), message: "Vui lòng nhập dữ liệu số!" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Nhập chuyển khoản thực tế"
            formatter={value => formatterInputNumber(value)}
            addonAfter="VNĐ"
            step={10000}
            min={0}
          />
        </Form.Item>

        <Form.Item label="Ghi chú:" name="notes">
          <TextArea placeholder="Ghi chú về chênh lệch (nếu có)" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default UpsertForm 